import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { hqAuthApi, HqStaff } from '../services/api/hqAuthApi';

// 메인 관리자 로그인 — 본부 포털(/hq) 직원 계정으로 들어온다. 옛 화면 암호(1983/0000)는 공개 JS 에 있어
// 잠금이 아니었다. 여기는 화면 안내용이고, 실제 차단은 nginx 와 서비스가 세션을 확인해서 한다.
// 서버에 확인할 수 없을 때(본부 포털이 잠깐 내려감)는 로그인 칸 대신 그 사실을 보여 준다 — 로그인 칸을 띄우면
// 이미 로그인한 사람이 자기 비밀번호를 의심하게 된다.
type GateState =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'error'; message: string }
  | { status: 'ok'; staff: HqStaff };

interface Props {
  children: (staff: HqStaff, logout: () => void) => React.ReactNode;
}

const AdminLoginGate: React.FC<Props> = ({ children }) => {
  const [state, setState] = useState<GateState>({ status: 'checking' });
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    setState({ status: 'checking' });
    try {
      setState({ status: 'ok', staff: await hqAuthApi.me() });
    } catch (err: any) {
      if (err.status === 401) setState({ status: 'anonymous' });
      else setState({ status: 'error', message: err.message });
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setBusy(true);
    try {
      const staff = await hqAuthApi.login(loginId.trim(), password);
      setPassword('');
      setState({ status: 'ok', staff });
    } catch (err: any) {
      // 서버 문구(틀림·잠김·형식)를 그대로 보여 준다
      setLoginError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = useCallback(async () => {
    await hqAuthApi.logout();
    setState({ status: 'anonymous' });
  }, []);

  if (state.status === 'ok') return <>{children(state.staff, logout)}</>;

  if (state.status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack spacing={2}>
          <Alert severity="error">{state.message}</Alert>
          <Button variant="contained" onClick={check}>다시 시도</Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ py: 8 }}>
      <Card>
        <CardContent>
          <form onSubmit={submit}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" gutterBottom>AWANA 관리자</Typography>
                <Typography variant="body2" color="text.secondary">
                  본부 포털(직원관리)에 등록된 계정으로 로그인합니다. 본부 포털과 같은 아이디·비밀번호입니다.
                </Typography>
              </Box>
              <TextField
                label="아이디"
                fullWidth
                autoFocus
                autoComplete="username"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
              />
              <TextField
                label="비밀번호"
                type="password"
                fullWidth
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {loginError && <Alert severity="warning">{loginError}</Alert>}
              <Button type="submit" variant="contained" disabled={busy || !loginId.trim() || !password}>로그인</Button>
              <Typography variant="body2" align="center">
                <Link href="/hq/enroll">아직 계정이 없으신가요? 본부 포털에서 등록키로 설정하기</Link>
              </Typography>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminLoginGate;
